import React, { useEffect, useState } from "react";
import ReactApexChart, { ApexOptions } from "react-apexcharts";
import moment from "moment";
import Big from "big.js";
import Notice from "../../../shared/Notice";
import Tooltip from "@funds/pages/MyFunds/parts/Tooltip";
import { Space } from "antd";
import { numberWithDelimiter } from "@features/utils/functions";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import { validDecimalNumber } from "@bundles/regexps";
import { FormikProps } from "formik";
import TimeLockStaking from "../../../TimelockStaking/TimelockStaking";

import * as style from "./ExpectedGainsChartStyle.module.scss";
import { useMediaQuery } from "react-responsive";
import { UnlockBonusesCardContent } from "@appComponents/UnlockBonusesCardContent/UnlockBonusesCardContent";

type Props = {
  amount?: string;
  arp: number;
  validatorDaysToActivate: string;
  formik: FormikProps<any>;
  isUk?: boolean;
  userTimelockBalance?: number;
  timelockMinValue?: number;
};

function calculateRewards(
  initialInvestment: any,
  annualPercentageRate: any,
  years: any
) {
  const rewards = [];
  let totalInvestment = initialInvestment;

  if (!validDecimalNumber.test(totalInvestment)) return [];

  const week1 = Big(totalInvestment).times(annualPercentageRate);
  const week2 = Big(week1).times(annualPercentageRate);
  const week3 = Big(week2).times(annualPercentageRate);
  const week4 = Big(week3).times(annualPercentageRate);

  for (let year = 1; year <= years; year++) {
    const yearlyReward = Big(totalInvestment)
      .times(annualPercentageRate)
      .toString();
    totalInvestment = Big(totalInvestment).plus(yearlyReward).toString();

    rewards.push([moment().add(year, "year").unix() * 1000, totalInvestment]);
  }

  return rewards;
}

const ExpectedGainsChart = ({
  amount,
  arp,
  validatorDaysToActivate,
  formik,
  isUk,
  userTimelockBalance,
  timelockMinValue,
}: Props) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [ethExchangeRate, setEthExchangeRate] = useState<number>();

  useEffect(() => {
    SecondaryMarketService.getExchange().then((resp) =>
      setEthExchangeRate(resp.data.usd_eth)
    );
  });

  const value = amount || 0;
  const APR = Big(arp).div(100);

  const rewards = calculateRewards(value, APR, 3);

  const state = {
    series: [
      {
        data: [[moment().unix() * 1000, amount], ...rewards],
      },
    ],
    options: {
      chart: {
        animations: {
          enabled: false,
        },
        id: "area-datetime",
        type: "area",
        height: 350,
        toolbar: {
          autoSelected: "pan",
          show: false,
        },
      },
      colors: ["#BE39F6"],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "straight",
      },
      markers: {
        size: 0,
        style: "hollow",
      },
      crosshairs: {
        fill: {
          type: "solid",
          color: "#B1B9C4",
        },
      },
      xaxis: {
        type: "category",
        labels: {
          formatter: function (val: string) {
            if (Number.isNaN(val)) return "0M";
            const months = moment(val).diff(moment(), "months") + 1;
            const label = months === 1 ? "Month" : "Months";
            return `${months} ${label}`;
          },
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        labels: {
          show: true,
          formatter: (value: number) => {
            if (Big(value).lte(1)) return Big(value).round(6).toString();
            return Big(value).round(2).toString();
          },
        },
      },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
          var data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];

          const title = () => {
            const months = moment(data[0]).diff(moment(), "months") + 1;
            return `In ${months} months, you would have approx:`;
          };

          const ethAmount =
            numberWithDelimiter(Big(data[1]).round(8).toString(), " ") + " ETH";
          const ethAmountInUsd =
            Big(data[1])
              .times(ethExchangeRate || 1)
              .toFixed(2)
              .toString() + " USD";

          return `<div>
            <div class="apexcharts-tooltip-title">${title()}</div>
            <div class="apexcharts-tooltip-series-group" style="display: flex; flex-direction: column; align-items: self-start;"> 
            <span class="apexcharts-tooltip-text-y-value">${ethAmount}</span>
            <span class="apexcharts-tooltip-text-y-value-in-usd">${ethAmountInUsd}</span>
            </div
            </div>`;
        },
      },

      marker: {
        show: false,
      },
      grid: {
        padding: {
          left: 15,
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          colorStops: [
            [
              {
                offset: 0,
                color: "#5f2ead19",
                opacity: 1,
              },
              {
                offset: 30,
                color: "#f1def920",
                opacity: 1,
              },
              {
                offset: 100,
                color: "white",
                opacity: 1,
              },
            ],
          ],
        },
      },
    },
    selection: "one_year",
  };

  return (
    <div className={style.wrapper}>
      <Notice
        description={
          <>
            <Space size={0}>
              Current estimated APR:&nbsp;<b>{arp.toString()}%</b>
              <Tooltip
                title=""
                getTooltipContainer={(triggerNode) =>
                  triggerNode.parentElement ?? triggerNode
                }
                tooltipMessage="This information comes from the Network. It may vary. This is for information only and you should not base your investment decisions on this information."
              />
            </Space>
            <p style={{ margin: 0 }}>
              Estimated time for a new Validator to become Active on the
              network: {validatorDaysToActivate}
            </p>
          </>
        }
        type="success"
        style={{
          margin: "40px auto",
          background: "#F9F9F9",
          border: "1px solid #E4E4E4",
          borderRadius: "10px",
        }}
      />

      {isMobile && (
        <div className="border border-[#E4E4E4] rounded-[12px] px-[20px] py-[30px] mb-[50px]">
          <p className="text-xs uppercase mb-2 font-bold">
            {isUk ? "Features" : "Bonuses"}
          </p>
          <UnlockBonusesCardContent
            isUk={isUk}
            timelockMinValue={timelockMinValue}
            timeLockBalance={userTimelockBalance}
          />
        </div>
      )}

      <TimeLockStaking isUk={isUk} displayNoTimeLock formik={formik} />

      {rewards.length > 0 && (
        <>
          <Space size={0}>
            <p className={style.title}>
              Estimation of expected Staking rewards
            </p>
            <Tooltip
              title=""
              getTooltipContainer={(triggerNode) =>
                triggerNode.parentElement ?? triggerNode
              }
              tooltipMessage="This is for informational purposes only. This consists of the principal + the estimated rewards."
            />
          </Space>
          <p className={style.axisYLabel}>ETH</p>
          <ReactApexChart
            options={state.options as ApexOptions}
            series={state.series as any}
            type="area"
            height={350}
          />
        </>
      )}
    </div>
  );
};

export default ExpectedGainsChart;
